/**
 * TTS Progress Tracker - localStorage utility for audio resume after refresh
 * 
 * Saves audio playback position to survive page refresh.
 * Uses cache_key (stable across stream recreate) instead of stream_id.
 * TTL: 5 minutes (enough for reconnect, doesn't clutter localStorage).
 */

const TTL_MS = 5 * 60 * 1000 // 5 minutes
const PREFIX = 'tts:progress:'

interface TtsProgressData {
  cacheKey: string
  currentTime: number
  savedAt: number
}

export function saveTtsProgress(cacheKey: string, currentTime: number): void {
  const key = PREFIX + cacheKey
  const data: TtsProgressData = {
    cacheKey,
    currentTime,
    savedAt: Date.now(),
  }
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (err) {
    console.warn('[ttsProgress] Failed to save:', err)
  }
}

export function getTtsProgress(cacheKey: string): number | null {
  const key = PREFIX + cacheKey
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const data = JSON.parse(raw) as TtsProgressData
    const age = Date.now() - data.savedAt

    if (age > TTL_MS) {
      localStorage.removeItem(key)
      return null
    }

    return data.currentTime
  } catch (err) {
    console.warn('[ttsProgress] Failed to get:', err)
    return null
  }
}

export function clearTtsProgress(cacheKey: string): void {
  const key = PREFIX + cacheKey
  try {
    localStorage.removeItem(key)
  } catch (err) {
    console.warn('[ttsProgress] Failed to clear:', err)
  }
}

export function cleanupExpiredTtsProgress(): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(PREFIX)) continue

      const raw = localStorage.getItem(key)
      if (!raw) continue

      const data = JSON.parse(raw) as TtsProgressData
      const age = Date.now() - data.savedAt

      if (age > TTL_MS) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))
    if (keysToRemove.length > 0) {
      console.log(`[ttsProgress] Cleaned up ${keysToRemove.length} expired entries`)
    }
  } catch (err) {
    console.warn('[ttsProgress] Cleanup failed:', err)
  }
}
